/**
 * REST client. Mirrors the process-runner setup:
 *   - Single AxiosInstance with response cache (axios-cache-interceptor)
 *   - Separate AxiosForRetry instance so retried requests bypass the cache
 *   - 401 handling redirects to the login flow
 *   - Idempotent retry on a known set of 5xx codes
 *
 * Auth header injection from stored login tokens when host-scoped cookies
 * are unavailable (mobileapp.* vs app.* cookie domain).
 */

import AxiosStatic, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { setupCache } from 'axios-cache-interceptor'
import { getIdToken, isValidSession } from './session'
import { MAX_RETRIES, RETRY_ERROR_CODES, RETRY_INTERVAL } from './retry-constants'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    retryCount?: number
    disableRetry?: boolean
    /** When true, a 401 does not clear session / redirect to /login. */
    skipAuthRedirect?: boolean
  }
  export interface AxiosRequestConfig {
    retryCount?: number
    disableRetry?: boolean
    skipAuthRedirect?: boolean
  }
}

const AxiosInstance = AxiosStatic.create({ withCredentials: true })
const AxiosForRetry = AxiosStatic.create({ withCredentials: true })

// Lazy import to avoid circular deps (auth.service -> axios -> auth.service).
async function handleUnauthorized() {
  const [{ redirectAfterUnauthorizedAccess }, { toastQueue }] = await Promise.all([
    import('./auth/auth.service'),
    import('@components/atoms/Toast'),
  ])
  toastQueue.add(
    { message: 'Your session has expired. Please sign in again.', variant: 'error' },
    { timeout: 5000 },
  )
  redirectAfterUnauthorizedAccess()
}

// Server-log a retryable request that ran out of retries. Lazy import mirrors
// handleUnauthorized to avoid the axios -> logging.service -> api/Logging ->
// axios cycle. The log call is skipped for logJSError itself (no recursion)
// and when there's no valid session. Failures are swallowed.
async function logRetriesExhausted(url: string, status?: number) {
  const { logError, logApiError, JSError } = await import('./logging.service')
  logError('retries exhausted - ' + url + ' ' + status)
  if (url && !url.includes('logJSError') && isValidSession()) {
    logApiError(JSError.ApiMaxRetryReached).catch(() => {})
  }
}

// Endpoints that legitimately return 401 without meaning "session expired"
// (pre-login lookups). Mirrors process-runner's exclude list.
const PUBLIC_AUTH_PATHS =
  /\/auth\/(login|loginSettings|logoUrl|ssoLoginUrl|forgotPassword)|\/comm\/otp\//

function attachAuthHeader(request: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const idToken = getIdToken()
  if (!idToken) return request
  request.headers.set('Authorization', idToken)
  return request
}

AxiosInstance.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => attachAuthHeader(request),
  (err: AxiosError) => Promise.reject(err),
)

AxiosForRetry.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => attachAuthHeader(request),
  (err: AxiosError) => Promise.reject(err),
)

AxiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (err: AxiosError) => {
    const status = err.response?.status
    const url = err.config?.url ?? ''
    const cfg = err.config as InternalAxiosRequestConfig | undefined
    if (
      status === 401 &&
      !PUBLIC_AUTH_PATHS.test(url) &&
      !cfg?.skipAuthRedirect
    ) {
      void handleUnauthorized()
      return Promise.reject(err)
    }

    if (
      status &&
      RETRY_ERROR_CODES.includes(status as (typeof RETRY_ERROR_CODES)[number]) &&
      cfg &&
      !cfg.disableRetry
    ) {
      const next = (cfg.retryCount ?? 0) + 1
      cfg.retryCount = next
      if (next <= MAX_RETRIES) {
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            AxiosForRetry(cfg).then(resolve).catch(reject)
          }, RETRY_INTERVAL)
        })
      }
      // Retryable status but retries are exhausted — signal the server.
      void logRetriesExhausted(url, status)
    }
    return Promise.reject(err)
  },
)

// Wrap with response cache. TTL 1 minute. GET-only by default. Skip cache for
// requests with a query string (they're usually parameterised lookups where
// staleness costs more than the network call).
const axios = setupCache(AxiosInstance, {
  ttl: 60 * 1000,
  cachePredicate: {
    ignoreUrls: [/\?/],
  },
})

export default axios
